/**
 * NotFoundRoute — auth-aware 404. When a logged-in user hits an unknown URL
 * (typed by hand, external link, stale bookmark) we keep them INSIDE the app
 * with clear next-steps, not eject them to the public marketing site.
 *
 * Anonymous visitors are still routed to the landing page (unchanged).
 */
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { AlertCircle, ArrowLeft, LayoutDashboard } from 'lucide-react';

export default function NotFoundRoute() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="notfound-route">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <AlertCircle className="h-6 w-6 text-amber-600" />
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Page not found</h1>
        <p className="text-sm text-slate-500 mb-1">
          We couldn&apos;t find <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{pathname}</code>.
        </p>
        <p className="text-xs text-slate-400 mb-6">
          You&apos;re still signed in. This page may have moved or you may have followed an outdated link.
        </p>
        <div className="flex gap-2 justify-center">
          <Button
            variant="outline"
            onClick={() => window.history.length > 1 ? window.history.back() : null}
            data-testid="notfound-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Go back
          </Button>
          <Link to="/dashboard">
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="notfound-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
